"use client";

import { createContext, useContext, useState } from "react";

export type LoginStep = "login" | "register";

type OpenOptions = {
  // Where to send the visitor after a successful login/registration — e.g.
  // a signed-out "become a trainer" CTA sets this to the trainer onboarding link (/trainer-panel?onboarding=1) so
  // the panel can send them straight there instead of just refreshing
  // whatever page they clicked it from.
  redirectTo?: string;
  // Which form the panel opens on. A "become a trainer" CTA opens straight
  // on "register" — a brand-new visitor almost never already has an
  // account, so the login form would just be a wasted extra click.
  initialStep?: LoginStep;
  // A short explanation shown above the form, e.g. "Create an account
  // first to set up a trainer profile." Its presence is also what switches
  // the panel from the plain nav corner dropdown to a centered modal (see
  // LoginPanel) — a message important enough to explain deserves to be the
  // first thing the visitor sees, not a small corner popup.
  intro?: string;
  // Called right after a successful login/registration, instead of the
  // default redirectTo navigation — lets a caller resume whatever the
  // visitor was doing in place (e.g. re-submit a booking) rather than
  // sending them somewhere else and losing that in-progress state.
  onAuthenticated?: () => void;
};

const LoginPanelContext = createContext<{
  open: boolean;
  redirectTo: string | null;
  initialStep: LoginStep;
  intro: string | null;
  onAuthenticated: (() => void) | null;
  setOpen: (open: boolean, opts?: OpenOptions) => void;
} | null>(null);

export function LoginPanelProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [initialStep, setInitialStep] = useState<LoginStep>("login");
  const [intro, setIntro] = useState<string | null>(null);
  const [onAuthenticated, setOnAuthenticated] = useState<(() => void) | null>(null);

  function setOpenWithOptions(next: boolean, opts?: OpenOptions) {
    setOpen(next);
    setRedirectTo(next ? (opts?.redirectTo ?? null) : null);
    setInitialStep(next ? (opts?.initialStep ?? "login") : "login");
    setIntro(next ? (opts?.intro ?? null) : null);
    // Stored as a thunk (`() => fn`) — useState would otherwise call a
    // plain function value to derive the next state instead of storing it.
    setOnAuthenticated(next && opts?.onAuthenticated ? () => opts.onAuthenticated! : null);
  }

  return (
    <LoginPanelContext.Provider
      value={{ open, redirectTo, initialStep, intro, onAuthenticated, setOpen: setOpenWithOptions }}
    >
      {children}
    </LoginPanelContext.Provider>
  );
}

// Lets any page trigger the nav's login dropdown (e.g. a "become a trainer"
// CTA for signed-out visitors) without duplicating the login form UI.
export function useLoginPanel() {
  const ctx = useContext(LoginPanelContext);
  if (!ctx) throw new Error("useLoginPanel must be used within LoginPanelProvider");
  return ctx;
}
